'use es6';
/*
 * Details relevant to a layout editing session.
 */

import { LAYOUT_EDITOR_SESSION_SET_WIDGET_SELECTIONS, LAYOUT_EDITOR_SESSION_CLEAR_WIDGET_SELECTIONS, LAYOUT_EDITOR_SESSION_ADD_WIDGET_SELECTION, LAYOUT_EDITOR_SESSION_REMOVE_WIDGET_SELECTION, LAYOUT_EDITOR_SESSION_TOGGLE_WIDGET_SELECTION, LAYOUT_EDITOR_SESSION_SCROLL_TO_WIDGET_SELECTIONS, LAYOUT_EDITOR_SESSION_WIDGET_CONTENT_EDITOR_OPEN, LAYOUT_EDITOR_SESSION_WIDGET_CONTENT_EDITOR_CLOSE, LAYOUT_EDITOR_SESSION_FULL_SCREEN_CODE_EDITOR_OPEN, LAYOUT_EDITOR_SESSION_FULL_SCREEN_CODE_EDITOR_CLOSE, LAYOUT_EDITOR_SESSION_WIDGET_SELECT_SMART_RULE, LAYOUT_EDITOR_SESSION_REVISION_ACTIVATE, LAYOUT_EDITOR_SESSION_REVISION_DEACTIVATE, LAYOUT_EDITOR_SESSION_BLOG_CONTENT_MODULE_EDITOR_OPEN, LAYOUT_EDITOR_SESSION_BLOG_CONTENT_MODULE_EDITOR_CLOSE, LAYOUT_EDITOR_SESSION_HEAD_BODY_EDITOR_OPEN, LAYOUT_EDITOR_SESSION_HEAD_BODY_EDITOR_CLOSE, LAYOUT_EDITOR_SESSION_SET_EDITING_LANGUAGE, LAYOUT_EDITOR_SESSION_SET_EDITOR_MODE } from 'DesignData/actions/ActionTypes';
import { getSelectedWidgets } from 'DesignData/selectors/layoutEditorSession';
import { POST } from 'DesignData/enums/BlogContentModuleTemplates';
import { DEFAULT } from 'DesignData/enums/EditorModes';
/****************************
 *
 * Layout editor mode
 *
 ***************************/

export function setEditorMode(layoutId, mode = DEFAULT) {
  return {
    type: LAYOUT_EDITOR_SESSION_SET_EDITOR_MODE,
    layoutId,
    mode
  };
}
/****************************
 *
 * Layout language editing
 *
 ***************************/

export function setEditingLanguage(layoutId, langCode) {
  return {
    type: LAYOUT_EDITOR_SESSION_SET_EDITING_LANGUAGE,
    layoutId,
    langCode
  };
}
/****************************
 *
 * Widget Selections
 *
 ***************************/

/**
 * Toggle selected widget.
 */

export function toggleWidgetSelection(layoutId, widgetId, isMultiSelect = false) {
  return (dispatch, getState) => {
    const state = getState();
    const prevSelectedWidgets = getSelectedWidgets(state, {
      layoutId
    });
    return dispatch({
      type: LAYOUT_EDITOR_SESSION_TOGGLE_WIDGET_SELECTION,
      layoutId,
      widgetId,
      isMultiSelect,
      prevSelectedWidgets
    });
  };
}
/**
 * Add selected widget.
 */

export function addWidgetSelection(layoutId, widgetId, isMultiSelect = false) {
  return (dispatch, getState) => {
    const state = getState();
    const prevSelectedWidgets = getSelectedWidgets(state, {
      layoutId
    });
    return dispatch({
      type: LAYOUT_EDITOR_SESSION_ADD_WIDGET_SELECTION,
      layoutId,
      widgetId,
      isMultiSelect,
      prevSelectedWidgets
    });
  };
}
/**
 * Remove selected widget.
 */

export function removeWidgetSelection(layoutId, widgetId, isMultiSelect = false) {
  return (dispatch, getState) => {
    const state = getState();
    const prevSelectedWidgets = getSelectedWidgets(state, {
      layoutId
    });
    return dispatch({
      type: LAYOUT_EDITOR_SESSION_REMOVE_WIDGET_SELECTION,
      layoutId,
      widgetId,
      isMultiSelect,
      prevSelectedWidgets
    });
  };
}
/**
 * Request scroll to first selected module (as determined by DOM).
 */

export function scrollToWidgetSelections() {
  return {
    type: LAYOUT_EDITOR_SESSION_SCROLL_TO_WIDGET_SELECTIONS
  };
}
/**
 * Set all widget selections.
 *
 * @param {String|Number}  layoutId
 * @param {Array.<String>} widgetIds
 * @param {Boolean}        scroll
 */

export function setWidgetSelections(layoutId, widgetIds, scroll = false) {
  return (dispatch, getState) => {
    const state = getState();
    const prevSelectedWidgets = getSelectedWidgets(state, {
      layoutId
    });
    return dispatch({
      type: LAYOUT_EDITOR_SESSION_SET_WIDGET_SELECTIONS,
      layoutId,
      widgetIds,
      prevSelectedWidgets,
      scroll: !!scroll
    });
  };
}
/**
 * Clear all widget selections.
 */

export function clearWidgetSelections(layoutId) {
  return (dispatch, getState) => {
    const state = getState();
    const prevSelectedWidgets = getSelectedWidgets(state, {
      layoutId
    });
    return dispatch({
      type: LAYOUT_EDITOR_SESSION_CLEAR_WIDGET_SELECTIONS,
      layoutId,
      prevSelectedWidgets
    });
  };
}
/****************************
 *
 * Revisions mode
 *
 ***************************/

/**
 * Sets revision mode as active for a layout.
 */

export function activateRevisionsMode(layoutId, revisionId) {
  return {
    type: LAYOUT_EDITOR_SESSION_REVISION_ACTIVATE,
    layoutId,
    revisionId
  };
}
/**
 * Sets revision mode as inactive for a layout.
 */

export function deactivateRevisionsMode(layoutId) {
  return {
    type: LAYOUT_EDITOR_SESSION_REVISION_DEACTIVATE,
    layoutId
  };
}
/****************************
 *
 * Widget Content Editor
 *
 ***************************/

/**
 * Open the WidgetContentEditor for a specified layout widget.
 */

export function openWidgetContentEditor(layoutId, widgetId, selectedField = null) {
  return {
    type: LAYOUT_EDITOR_SESSION_WIDGET_CONTENT_EDITOR_OPEN,
    layoutId,
    widgetId,
    selectedField
  };
}
/**
 * Close the WidgetContentEditor for a specified layout widget.
 */

export function closeWidgetContentEditor(layoutId, widgetId) {
  return {
    type: LAYOUT_EDITOR_SESSION_WIDGET_CONTENT_EDITOR_CLOSE,
    layoutId,
    widgetId
  };
}
/****************************
 *
 * Full Screen Code Editor
 *
 ***************************/

/**
 * Open full screen editor for html fields
 */

export function openFullScreenCodeEditor(layoutId, widgetId, fieldProps) {
  return {
    type: LAYOUT_EDITOR_SESSION_FULL_SCREEN_CODE_EDITOR_OPEN,
    layoutId,
    widgetId,
    fieldProps
  };
}
export function closeFullScreenCodeEditor(layoutId, widgetId) {
  return {
    type: LAYOUT_EDITOR_SESSION_FULL_SCREEN_CODE_EDITOR_CLOSE,
    layoutId,
    widgetId
  };
}
/**
 * Open full screen editor for head/body options
 */

export function openFullScreenHeadBodyEditor(layoutId, selectedField) {
  return {
    type: LAYOUT_EDITOR_SESSION_HEAD_BODY_EDITOR_OPEN,
    layoutId,
    selectedField
  };
}
export function closeFullScreenHeadBodyEditor(layoutId) {
  return {
    type: LAYOUT_EDITOR_SESSION_HEAD_BODY_EDITOR_CLOSE,
    layoutId
  };
}
/****************************
 *
 * Blog content module templates editor
 *
 ***************************/

/**
 * Open blog content template editor for specific blo content module
 */

export function openBlogContentModuleTemplatesEditor(layoutId, widgetId, selectedTemplate = POST) {
  return {
    type: LAYOUT_EDITOR_SESSION_BLOG_CONTENT_MODULE_EDITOR_OPEN,
    layoutId,
    widgetId,
    selectedTemplate
  };
}
/**
 * Close blog content template editor for specific blo content module
 */

export function closeBlogContentModuleTemplatesEditor(layoutId, widgetId) {
  return {
    type: LAYOUT_EDITOR_SESSION_BLOG_CONTENT_MODULE_EDITOR_CLOSE,
    layoutId,
    widgetId
  };
}
/****************************
 *
 * Widget Smart Content Editor
 *
 ***************************/

/**
 * Set selected smart rule for a specified layout widget.
 */

export function selectWidgetSmartRule(layoutId, widgetId, selectedRuleId) {
  return {
    type: LAYOUT_EDITOR_SESSION_WIDGET_SELECT_SMART_RULE,
    layoutId,
    widgetId,
    selectedRuleId
  };
}